import {
  Button,
  Collapse,
  Grid,
  IconButton,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core'
import {
  Add,
  ChevronRight,
  InsertDriveFile,
  MoreVert,
  Search,
} from '@material-ui/icons'
import { saveAs } from 'file-saver'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { downloadAll, downloadFile } from '../../../actions/fileActions'
import { updateMachine } from '../../../actions/machineActions'
import { updatePart } from '../../../actions/partActions'
import { updateProgram } from '../../../actions/programActions'
import {
  deleteProjectFile,
  getProjectFiles,
} from '../../../actions/projectActions'
import filterIcon from '../../../assets/img/icons/filter.svg'
import {
  FILE_DOWNLOAD_ALL_RESET,
  FILE_DOWNLOAD_RESET,
} from '../../../constants/fileConstants'
import { MACHINE_UPDATE_RESET } from '../../../constants/machineConstants'
import { SIDE_NAV_CLOSE } from '../../../constants/navigationConstants'
import { PART_UPDATE_RESET } from '../../../constants/partConstants'
import { PROGRAM_UPDATE_RESET } from '../../../constants/programConstants'
import { PROJECT_FILE_DELETE_RESET } from '../../../constants/projectConstants'
import { useFileInfo } from '../../../hooks/useFileInfo'
import { useInput } from '../../../hooks/useInput'
import ActionsMenu from '../../ActionsMenu/ActionsMenu'
import FileUploadTable from '../../Form/FileUploadTable'
import MultiFilesInput from '../../Form/MultiFilesInput'
import SearchInput from '../../Form/SearchInput'
import SelectInput from '../../Form/SelectInput'
import Loader from '../../Loader/Loader'
import Message from '../../Message/Message'
import ModalPopUp from '../../ModalPopUp/ModalPopUp'
import ToastMessage from '../../ToastMessage/ToastMessage'
import OverflowTooltip from '../../Tooltip/OverflowTooltip'

const ProjectAttachments = () => {
  const dispatch = useDispatch()

  const { project } = useSelector((state) => state.projectDetails)
  const { userInfo } = useSelector((state) => state.userLogin)
  const {
    loading: filesLoading,
    error: projectFilesError,
    items,
  } = useSelector((state) => state.projectFiles)
  const { success: partUpdateSuccess } = useSelector(
    (state) => state.partUpdate
  )
  const { success: machineUpdateSuccess } = useSelector(
    (state) => state.machineUpdate
  )
  const { success: programUpdateSuccess } = useSelector(
    (state) => state.programUpdate
  )
  const { success: fileDeleteSuccess } = useSelector(
    (state) => state.projectFileDelete
  )
  const { file, fileName } = useSelector((state) => state.fileDownload)
  const { file: allFile, fileName: allFileName } = useSelector(
    (state) => state.fileDownloadAll
  )

  const [showAttachmentModal, setShowAttachmentModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [fileToDelete, setFileToDelete] = useState(false)
  const [files, setFiles] = useState([])
  const [filesUploading, setFilesUploading] = useState([])
  const [filesError, setFilesError] = useState('')
  const [fileContext, setFileContext] = useState({})
  const [toast, setToast] = useState(false)
  const [downloadToast, setDownloadToast] = useState(false)
  const [fileLength, setFileLength] = useState(0)
  const [showSearch, setShowSearch] = useState(false)
  const [showMobileSearch, setShowMobileSearch] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [fileTypes, setFileTypes] = useState([])
  const [downloadList, setDownloadList] = useState([])
  const [downloadAllDisabled, setDownloadAllDisabled] = useState(true)

  const {
    bind: bindSearch,
    value: searchValue,
    setValue: setSearchValue,
  } = useInput('')
  const {
    bind: bindFilterType,
    value: filterType,
    setValue: setFilterType,
  } = useInput('')

  useEffect(() => {
    if (project._id) {
      dispatch(getProjectFiles(project._id))
    }

    if (partUpdateSuccess || machineUpdateSuccess || programUpdateSuccess) {
      setShowAttachmentModal(false)
      setFiles([])
      setToast(true)
      dispatch({ type: PART_UPDATE_RESET })
      dispatch({ type: MACHINE_UPDATE_RESET })
      dispatch({ type: PROGRAM_UPDATE_RESET })
      dispatch({ type: SIDE_NAV_CLOSE })
    }

    if (fileDeleteSuccess) {
      setShowDeleteModal(false)
      dispatch({ type: PROJECT_FILE_DELETE_RESET })
    }

    if (file) {
      saveAs(file, fileName)
      dispatch({ type: FILE_DOWNLOAD_RESET })
    }

    if (allFile) {
      saveAs(allFile, allFileName)
      dispatch({ type: FILE_DOWNLOAD_ALL_RESET })
    }
  }, [
    file,
    dispatch,
    project,
    partUpdateSuccess,
    machineUpdateSuccess,
    programUpdateSuccess,
    fileDeleteSuccess,
    fileName,
    allFile,
    allFileName,
  ])

  useEffect(() => {
    // 1. Calc number of attachments in the project by each part, machine, program
    // 2. Create a list of file extensions to use for filtering

    let numOfFiles = 0
    let fileExtensions = []
    let fileList = []

    const processFileArray = (arr) => {
      // eslint-disable-next-line array-callback-return
      arr.map((x) => {
        fileList.push({ id: x.id, name: x.name })
        fileExtensions.push(/[^.]*$/.exec(x.name)[0].toLowerCase())
      })
    }

    if (items && items.length) {
      // eslint-disable-next-line array-callback-return
      items.map((item, index) => {
        if (item.type === 'part') {
          numOfFiles =
            numOfFiles +
            item.partProgramFiles.length +
            item.additionalDocuments.length

          processFileArray(item.partProgramFiles)
          processFileArray(item.additionalDocuments)
        }

        if (item.type === 'machine') {
          numOfFiles = numOfFiles + item.machineFiles.length

          processFileArray(item.machineFiles)
        }

        if (item.type === 'program') {
          numOfFiles =
            numOfFiles + item.programFiles.length + item.additionalFiles.length

          processFileArray(item.programFiles)
          processFileArray(item.additionalFiles)
        }
      })

      fileExtensions = [...new Set(fileExtensions)]

      setDownloadAllDisabled(fileList.length === 0)
      setFileTypes(fileExtensions)
      setFileLength(numOfFiles)
      setDownloadList({ fileList })
    }
  }, [items])

  const handleCloseAttachmentModal = () => {
    setFiles([])
    setShowAttachmentModal(false)
  }

  const handleCloseSearch = () => {
    setSearchValue('')
    setShowSearch(false)
    setShowMobileSearch(false)
  }

  const handleDownloadAllClick = () => {
    setDownloadToast(true)
    dispatch(downloadAll(downloadList))
  }

  const handleUpdateItemClick = () => {
    const event = {
      event: 'New document',
      user: userInfo._id,
      project: project._id,
    }

    if (fileContext.type === 'part') {
      let partClone = fileContext.item
      partClone.event = event

      if (fileContext.arrayName === 'Program and CAD files') {
        partClone.partProgramFiles.push(...files)
        dispatch(updatePart(partClone))
      }

      if (fileContext.arrayName === 'Additional documents') {
        partClone.additionalDocuments.push(...files)
        dispatch(updatePart(partClone))
      }
    }

    if (fileContext.type === 'machine') {
      let machineClone = fileContext.item
      machineClone.event = event

      machineClone.machineFiles.push(...files)
      dispatch(updateMachine(machineClone))
    }

    if (fileContext.type === 'program') {
      let programClone = fileContext.item
      programClone.event = event

      if (fileContext.arrayName === 'Program Files') {
        programClone.programFiles.push(...files)
        dispatch(updateProgram(programClone))
      }

      if (fileContext.arrayName === 'Additional files') {
        programClone.additionalFiles.push(...files)
        dispatch(updateProgram(programClone))
      }
    }
  }

  const handleClear = () => {
    setFilterType('')
  }

  const deleteFile = () => {
    dispatch(deleteProjectFile(fileToDelete))
  }

  const handleMobileSearchClick = () => {
    setShowMobileSearch(!showMobileSearch)
    setShowFilters(false)
  }

  const Item = ({ item }) => {
    const [expanded, setExpanded] = useState(true)

    return (
      <div className="item">
        <div className="flex align-center">
          <IconButton onClick={() => setExpanded(!expanded)} className="small">
            <ChevronRight
              className={expanded ? 'rotate' : ''}
              style={{ color: '#666666' }}
            />
          </IconButton>
          <div className="primary-text strong m-l-5">
            <span className="capitalize">{item.type}</span> | {item.name}
          </div>
        </div>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          {item.type === 'part' && <PartFiles part={item} />}
          {item.type === 'machine' && <MachineFiles machine={item} />}
          {item.type === 'program' && <ProgramFiles program={item} />}
        </Collapse>
      </div>
    )
  }

  const PartFiles = ({ part }) => {
    const handleAddFileClick = (arrayName) => {
      setFileContext({
        item: part,
        type: part.type,
        name: part.name,
        arrayName,
      })
      setShowAttachmentModal(true)
    }

    return (
      <div>
        <div className="flex space-between align-center gray-text fs14 m-t-16 m-b-6">
          <div className="">Part drawing file(s)</div>
          <div
            className="strong m-t-6 m-b-6 primary-text fs12 flex align-center cursor letter-spacing"
            onClick={() => handleAddFileClick('Program and CAD files')}
          >
            <Add className="hide-580" /> Add file
          </div>
        </div>

        {part.partProgramFiles &&
          part.partProgramFiles.map((file, index) => (
            <File
              key={index}
              file={file}
              index={index}
              arrayName="partProgramFiles"
              type="part"
              itemId={part._id}
            />
          ))}

        <div className="flex space-between align-center gray-text fs14 m-t-16 m-b-6">
          <div>Program, CAD, or other documents</div>
          <div
            className="strong m-t-6 m-b-6 primary-text fs12 flex align-center cursor letter-spacing"
            onClick={() => handleAddFileClick('Additional documents')}
          >
            <Add className="hide-580" /> Add file
          </div>
        </div>

        {part.additionalDocuments &&
          part.additionalDocuments.map((file, index) => (
            <File
              key={index}
              file={file}
              index={index}
              arrayName="additionalDocuments"
              type="part"
              itemId={part._id}
            />
          ))}
      </div>
    )
  }

  const MachineFiles = ({ machine }) => {
    const handleAddFileClick = (arrayName) => {
      setFileContext({
        item: machine,
        type: 'machine',
        name: machine.name,
        arrayName,
      })
      setShowAttachmentModal(true)
    }

    return (
      <>
        <div className="flex space-between align-center gray-text fs14 m-t-16 m-b-6">
          <div>Machine files</div>
          <div
            className="strong m-t-6 m-b-6 primary-text fs12 flex align-center cursor letter-spacing"
            onClick={() => handleAddFileClick('Uploads')}
          >
            <Add className="hide-580" /> Add file
          </div>
        </div>

        {machine.machineFiles &&
          machine.machineFiles.map((file, index) => (
            <File
              key={index}
              file={file}
              index={index}
              type="machine"
              itemId={machine._id}
            />
          ))}
      </>
    )
  }

  const ProgramFiles = ({ program }) => {
    const handleAddFileClick = (arrayName) => {
      setFileContext({
        item: program,
        type: 'program',
        name: program.name,
        arrayName,
      })
      setShowAttachmentModal(true)
    }

    return (
      <>
        <div className="flex space-between align-center gray-text fs14 m-t-16 m-b-6">
          <div>Program files</div>
          <div
            className="strong m-t-6 m-b-6 primary-text fs12 flex align-center cursor letter-spacing"
            onClick={() => handleAddFileClick('Program Files')}
          >
            <Add className="hide-580" /> Add file
          </div>
        </div>

        {program.programFiles &&
          program.programFiles.map((file, index) => (
            <File
              key={index}
              file={file}
              index={index}
              type="program"
              arrayName="programFiles"
              itemId={program._id}
            />
          ))}

        <div className="flex space-between align-center gray-text fs14 m-t-16 m-b-6">
          <div>Additional files</div>
          <div
            className="strong m-t-6 m-b-6 primary-text fs12 flex align-center cursor letter-spacing"
            onClick={() => handleAddFileClick('Additional files')}
          >
            <Add className="hide-580" /> Add file
          </div>
        </div>

        {program.additionalFiles &&
          program.additionalFiles.map((file, index) => (
            <File
              key={index}
              file={file}
              index={index}
              type="program"
              arrayName="additionalFiles"
              itemId={program._id}
            />
          ))}
      </>
    )
  }

  const File = ({ file, arrayName, index, type, itemId }) => {
    const { fileSize, fileName, fileType } = useFileInfo(file)

    const handleDownloadClick = () => {
      setDownloadToast(true)
      dispatch(downloadFile(file))
    }

    const handleDeleteClick = (file) => {
      setFileToDelete({ file, name: file.name, type, arrayName, index, itemId })
      setShowDeleteModal(true)
    }

    return (
      file.name.toLowerCase().includes(searchValue) &&
      file.name.toLowerCase().includes(filterType) && (
        <div className="file-row">
          <div
            className="flex align-center p-r-16 p-l-8 "
            style={{ width: '50%' }}
          >
            <InsertDriveFile
              className="m-r-5 hide-580"
              style={{ fontSize: 16 }}
            />
            <OverflowTooltip title={fileName} />
          </div>
          <div style={{ width: '20%' }}>{fileSize}</div>
          <div style={{ width: '20%' }}>{fileType}</div>
          <div className="text-right" style={{ width: '10%' }}>
            <ActionsMenu
              icon={
                <MoreVert style={{ width: 16, height: 16, color: 'black' }} />
              }
              options={[
                {
                  onClick: () => handleDownloadClick(file.id),
                  label: 'Download',
                },
                { onClick: () => handleDeleteClick(file), label: 'Delete' },
              ]}
            />
          </div>
        </div>
      )
    )
  }

  return (
    <div className="attachments">
      {filesLoading ? (
        <Loader />
      ) : (
        <>
          <div className="header">
            <div className="strong fs16">Attachments</div>
            <div className="flex" style={{ maxHeight: 33 }}>
              {showSearch ? (
                <SearchInput
                  bindValue={bindSearch}
                  setShowSearch={handleCloseSearch}
                  className="m-r-5 hide-580"
                />
              ) : (
                <IconButton
                  onClick={() => setShowSearch(true)}
                  className="m-r-5 hide-580"
                >
                  <Search />
                </IconButton>
              )}

              <IconButton
                style={{ width: 33 }}
                onClick={() => setShowFilters(!showFilters)}
                className="hide-580"
              >
                <img style={{ width: 20 }} src={filterIcon} alt="Filter" />
              </IconButton>

              <Button
                variant="contained"
                color="primary"
                className="m-l-12 hide-580"
                onClick={handleDownloadAllClick}
                disabled={downloadAllDisabled}
              >
                Download all
              </Button>

              <ActionsMenu
                className="show-580"
                icon={<MoreVert />}
                options={[
                  {
                    onClick: handleMobileSearchClick,
                    label: 'Search',
                  },
                  {
                    onClick: () => setShowFilters(!showFilters),
                    label: 'Filter',
                  },
                  {
                    onClick: handleDownloadAllClick,
                    label: 'Download all',
                  },
                ]}
              />
            </div>
          </div>

          {showMobileSearch && (
            <div className="filter-bar">
              <div className="form-container p-l-0 p-r-0">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <SearchInput
                      bindValue={bindSearch}
                      setShowSearch={handleCloseSearch}
                      className="m-r-5 hide-580"
                    />
                  </Grid>
                </Grid>
              </div>
            </div>
          )}

          {showFilters && (
            <div className="filter-bar">
              <div className="flex space-between align-center">
                <label className="fs14 strong">Filters</label>
                <span
                  className="link fs12"
                  rel="noopener noreferrer"
                  target="_blank"
                  onClick={handleClear}
                >
                  Clear
                </span>
              </div>
              <div className="form-container p-l-0 p-r-0">
                <Grid container spacing={2}>
                  <Grid item lg={4} md={6} sm={6} xs={12}>
                    <SelectInput
                      name="type"
                      label="File type"
                      options={fileTypes.map((x) => ({ value: x, label: x }))}
                      bindValue={bindFilterType}
                    />
                  </Grid>
                </Grid>
              </div>
            </div>
          )}
          <TableContainer>
            <Table stickyHeader aria-label="custom pagination table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <div className="flex">
                      <div style={{ width: '50%' }}>File name</div>
                      <div style={{ width: '20%' }}>Size</div>
                      <div style={{ width: '20%' }}>Type</div>
                      <div style={{ width: '10%' }}></div>
                    </div>
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>

          <div className="items-table">
            {items &&
              items.map((item, index) => <Item key={index} item={item} />)}
          </div>

          {projectFilesError && (
            <Message variant="danger p-16">{projectFilesError}</Message>
          )}

          <div className="footer">
            <div>{fileLength} files</div>
          </div>
        </>
      )}

      <ModalPopUp
        title="Attachments"
        setShowModal={handleCloseAttachmentModal}
        showModal={showAttachmentModal}
        className="form-container"
      >
        <h4>Add file</h4>

        <div className="form-container">
          <MultiFilesInput
            filesUploading={filesUploading}
            setFiles={setFiles}
            setFilesUploading={setFilesUploading}
            contentTop={
              <>
                Drag and drop or click to add
                <strong> helpful files</strong>
              </>
            }
            error={filesError}
            setError={setFilesError}
          />

          {(files.length > 0 || filesUploading.length > 0) && (
            <FileUploadTable
              data={files}
              setData={setFiles}
              filesUploading={filesUploading}
            />
          )}
          <p className="fs12">
            These files will be added to the section{' '}
            <strong>{fileContext.arrayName}</strong> of the {fileContext.type}{' '}
            <strong>{fileContext.name}</strong>
          </p>

          <div className="flex flex-end m-t-40">
            <Button
              color="primary"
              variant="outlined"
              className="m-r-24"
              onClick={handleCloseAttachmentModal}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={handleUpdateItemClick}
            >
              Add
            </Button>
          </div>
        </div>
      </ModalPopUp>

      <ModalPopUp
        title="Delete document"
        setShowModal={setShowDeleteModal}
        showModal={showDeleteModal}
      >
        <h4>Delete document</h4>

        <p>Are you sure you want to delete {fileToDelete.name}?</p>

        <div className="flex flex-end m-t-40">
          <Button
            color="primary"
            variant="outlined"
            className="m-r-24"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={deleteFile}>
            Yes, delete
          </Button>
        </div>
      </ModalPopUp>

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Files added</p>
            <p className="fs14 m-b-0">
              Your files have been successfully added to attachements.
            </p>
          </>
        }
        open={toast}
        setOpen={setToast}
      />

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Downloading</p>
            <p className="fs14 m-b-0">
              We are preparing your files for download. Please wait...
            </p>
          </>
        }
        open={downloadToast}
        setOpen={setDownloadToast}
      />
    </div>
  )
}

export default ProjectAttachments
